import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "react-query";
import { enqueueSnackbar } from "notistack";
import { Divider, Skeleton, Typography } from "@mui/material";

import AccordionTemplate from "../AccordionTemplate";
import {
  ConfigurationTableFilter,
  ConfigurationTableFilterType,
  SelectorAreaType,
} from "../../../Configuration/interfaces";
import { Filters } from "../../../Configuration/Filter";
import { basicFetch, GenericObject } from "../../../services/Price";
import MailsList from "./MailsList";
import { StandardGenericPaginatedResponse } from "../../../Dashboard/services";
import MailsCreation from "./MailsCreation";
import { updateToggleMails } from "./utils";

interface EmailType {
  id: number;
  perimeters: GenericObject[];
  email: string;
  first_name: string;
  language: string;
  analysis: string[];
  granularity: null;
  start: string;
  end: string;
  is_active: boolean;
}

const filters: ConfigurationTableFilter[] = [
  {
    type: ConfigurationTableFilterType.StaticSelect,
    key: "block_name",
    label: "BLOC_MAIL",
    lookup: "analysis",
  },
  {
    type: ConfigurationTableFilterType.DatePicker,
    lookup: "start",
    label: "START_DATE",
  },
  {
    type: ConfigurationTableFilterType.DatePicker,
    lookup: "end",
    label: "END_DATE",
  },
  {
    type: ConfigurationTableFilterType.StaticSelect,
    key: "granularity",
    label: "ZONE",
    lookup: "granularity",
  },
  {
    type: ConfigurationTableFilterType.SelectMultipleApi,
    maxTag: 1,
    controlledInputSize: true,
    key: "perimeters",
    label: "SPATIAL_PERIMETER",
    labelKey: "",
    api: "",
    lookup: "perimeters",
    watchLookup: "granularity",
    multiApi: {
      watchFilter: "granularity",
      apisFieldMap: {
        ALL: null,
        PROPERTY: "property",
        ZONE_GEO_1: "geozone1",
        ZONE_GEO_2: "geozone2",
      },
      apis: [
        {
          api: "",
          caseOfValueIs: SelectorAreaType.ALL,
          labelKey: "",
          defaultValue: "ALL",
        },
        {
          api: "/api/properties/",
          apiParams: {
            page_size: 100,
            ordering: "hebergement_nom_court",
          },
          caseOfValueIs: SelectorAreaType.PROPERTY,
          labelKey: "hebergement_nom_court",
        },
        {
          api: "/api/property-geo-zones-1/",
          apiParams: {
            page_size: 100,
            ordering: "short_name",
          },
          caseOfValueIs: SelectorAreaType.GEO_AREA_1,
          labelKey: "short_name",
        },
        {
          api: "/api/property-geo-zones-2/",
          apiParams: {
            page_size: 100,
            ordering: "short_name",
          },
          caseOfValueIs: SelectorAreaType.GEO_AREA_2,
          labelKey: "short_name",
        },
      ],
    },
  },
];

const MailsAccordion = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [filterValue, setFilterValue] = useState<any>({});
  const [mailActivated, setMailActivated] = useState<boolean | null>(null);

  const mailsListQuery: UseQueryResult<
    StandardGenericPaginatedResponse<EmailType>,
    unknown
  > = useQuery(["notifications-mails-list", JSON.stringify(filterValue)], () =>
    basicFetch(
      `/api/user/bloc-mails/`,
      { ...filterValue },
      {
        methode: "GET",
      }
    )
  );

  const toggleMailingQuery = useMutation(
    ["notifications-mails-toggle"],
    (data: GenericObject) =>
      updateToggleMails(`/api/user/bloc-mails/toggle-email/`, data, {
        method: "POST",
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("notifications-mails-list");
        enqueueSnackbar(intl.formatMessage({ id: "OPERATION_SUCCESS" }), {
          variant: "success",
        });
      },
      onError: () => {
        enqueueSnackbar(intl.formatMessage({ id: "OPERATION_FAILED" }), {
          variant: "error",
        });
      },
    }
  );

  const handleActivateMail = useCallback(
    (isActive: boolean) => {
      toggleMailingQuery.mutate({ is_active: isActive });
    },
    [toggleMailingQuery]
  );

  const handleFiltersChange = useCallback((filters: GenericObject) => {
    setFilterValue(filters.filter);
  }, []);

  useEffect(() => {
    if (mailsListQuery.data?.results) {
      const disabledMail = mailsListQuery.data.results.some(
        (mail) => !mail.is_active
      );

      setMailActivated(!disabledMail);
    }
  }, [mailsListQuery.data?.results]);

  return (
    <AccordionTemplate
      checked={mailActivated}
      title="BLOC_MAIL"
      accordionName="mail"
      switchLabel="NOTIFICATION_ACTIVATE_MAIL"
      onSwitchChange={handleActivateMail}
    >
      <>
        <MailsCreation />
        <Divider
          sx={{
            borderColor: "background.default",
            mt: 1,
            mb: 2,
          }}
        />
        <>
          <Typography>
            {intl.formatMessage({ id: "NOTIFICATION_MAILS" })}
          </Typography>
          <Filters
            tableFilter={filters}
            onChange={handleFiltersChange}
            contrastedStyles
            datePickerVariant="outlined"
          />
          {mailsListQuery?.data?.results &&
            mailsListQuery.data.results.length === 0 &&
            !mailsListQuery.isLoading && (
              <Typography sx={{ color: "text.secondary" }}>
                {intl.formatMessage({ id: "NOTIFICATION_NO_MAILS" })}
              </Typography>
            )}
          {mailsListQuery.isLoading && (
            <Skeleton variant="rounded" width="100%" height="140px" />
          )}
          {mailsListQuery.isError && (
            <Typography sx={{ color: "text.secondary" }}>
              {intl.formatMessage({ id: "SOMETHING_WENT_WRONG" })}
            </Typography>
          )}
          {mailsListQuery?.data?.results &&
            mailsListQuery.data.results.length > 0 && (
              <MailsList mailsList={mailsListQuery.data.results} />
            )}
        </>
      </>
    </AccordionTemplate>
  );
};

export default MailsAccordion;
